import { getOrFetchToken } from "./auth";
import * as types from "./types";

export interface EntriesResponse {
  entries: types.Entry[];
  isMore: boolean;
}

interface EntryResponse {
  entry: types.Entry;
}

interface ThemesResponse {
  themes: types.EntryTheme[];
}

interface KeywordsResponse {
  keywords: types.KeywordItem[];
}

export interface ReferencesListAPIResponse {
  results: types.ReferenceCard[];
  next: string | null;
  previous: string | null;
}

export interface EvergreensListAPIResponse {
  results: types.EvergreenCard[];
  next: string | null;
  previous: string | null;
}

function fullUrl(endpoint: string): string {
  if (process.env.API_HOSTNAME) {
    return `${process.env.API_HOSTNAME}${endpoint}`;
  } else {
    return `/api${endpoint}`;
  }
}

function fullAuthUrl(endpoint: string): string {
  return `${process.env.AUTH_HOSTNAME}${endpoint}`;
}

export async function getRequest(url: string): Promise<Response> {
  return fetch(url, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${await getOrFetchToken()}`,
    },
  });
}

interface ListEntriesQuery {
  entryType?: string;
  interval?: string;
  keyword?: string;
  cursor?: string;
}

export async function listEntries(query: ListEntriesQuery): Promise<EntriesResponse> {
  const queryParams = new URLSearchParams();
  for (const [key, val] of Object.entries(query)) {
    if (val) {
      queryParams.append(key, val);
    }
  }
  let url = "/entries";
  if (queryParams.toString() !== "") {
    url = url + "?" + queryParams.toString();
  }
  const resp = await fetch(fullUrl(url), {
    method: "GET",
    headers: {
      Authorization: `Bearer ${await getOrFetchToken()}`,
    },
  });
  const entries = await resp.json();
  return entries;
}

export async function createEntry(inputs: types.EntryInputs): Promise<EntryResponse> {
  const resp = await fetch(fullUrl("/entries"), {
    method: "POST",
    headers: {
      Authorization: `Bearer ${await getOrFetchToken()}`,
    },
    body: JSON.stringify(inputs),
  });
  const data = await resp.json();
  return data;
}

export async function retrieveEntry(entryId: string): Promise<EntryResponse> {
  const resp = await fetch(fullUrl(`/entries/${entryId}`), {
    method: "GET",
    headers: {
      Authorization: `Bearer ${await getOrFetchToken()}`,
    },
  });
  const data = await resp.json();
  return data;
}

export async function addEntryLink(entryId: string, entryIdToLink: string): Promise<EntryResponse> {
  const resp = await fetch(fullUrl(`/entries/${entryId}/references/${entryIdToLink}`), {
    method: "PUT",
    headers: {
      Authorization: `Bearer ${await getOrFetchToken()}`,
    },
  });
  const data = await resp.json();
  return data;
}

export async function updateEntry(
  entryId: string,
  inputs: types.EntryInputs
): Promise<EntryResponse> {
  const resp = await fetch(fullUrl(`/entries/${entryId}`), {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${await getOrFetchToken()}`,
    },
    body: JSON.stringify(inputs),
  });
  const data = await resp.json();
  return data;
}

export async function listThemes(): Promise<ThemesResponse> {
  const resp = fetch(fullUrl("/themes"), {
    method: "GET",
    headers: {
      Authorization: `Bearer ${await getOrFetchToken()}`,
    },
  });
  const data = (await resp).json();
  return data;
}

export async function createTheme(inputs: any): Promise<Response> {
  return fetch(fullUrl("/themes"), {
    method: "POST",
    headers: {
      Authorization: `Bearer ${await getOrFetchToken()}`,
    },
    body: JSON.stringify(inputs),
  });
}

export async function listKeywords(): Promise<KeywordsResponse> {
  const resp = await fetch(fullUrl("/keywords"), {
    method: "GET",
    headers: {
      Authorization: `Bearer ${await getOrFetchToken()}`,
    },
  });
  const data = await resp.json();
  return data;
}

export async function listKeywordsNew(): Promise<Response> {
  const resp = await fetch(fullAuthUrl("/keywords/"), {
    method: "GET",
    headers: {
      Authorization: `Bearer ${await getOrFetchToken()}`,
    },
  });
  return resp;
}

export async function listReferences(query: ListEntriesQuery): Promise<ReferencesListAPIResponse> {
  const queryParams = new URLSearchParams();
  for (const [key, val] of Object.entries(query)) {
    if (val) {
      queryParams.append(key, val);
    }
  }
  let url = "/references/";
  if (queryParams.toString() !== "") {
    url = url + "?" + queryParams.toString();
  }
  const resp = await fetch(fullAuthUrl(url), {
    method: "GET",
    headers: {
      Authorization: `Bearer ${await getOrFetchToken()}`,
    },
  });
  const entries = await resp.json();
  return entries;
}

export async function listEvergreens(query: ListEntriesQuery): Promise<EvergreensListAPIResponse> {
  const queryParams = new URLSearchParams();
  for (const [key, val] of Object.entries(query)) {
    if (val) {
      queryParams.append(key, val);
    }
  }
  let url = "/evergreens/";
  if (queryParams.toString() !== "") {
    url = url + "?" + queryParams.toString();
  }
  const resp = await fetch(fullAuthUrl(url), {
    method: "GET",
    headers: {
      Authorization: `Bearer ${await getOrFetchToken()}`,
    },
  });
  const entries = await resp.json();
  return entries;
}

export async function createEvergreen(inputs: types.EvergreenCardInputs): Promise<Response> {
  const resp = await fetch(fullAuthUrl("/evergreens/"), {
    method: "POST",
    headers: {
      Authorization: `Bearer ${await getOrFetchToken()}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(inputs),
  });
  return resp;
}

export async function createInboxNote(url: string, text: string): Promise<Response> {
  const inputs = {
    url,
    text,
  };
  const resp = await fetch(fullAuthUrl("/inbox-notes/"), {
    method: "POST",
    headers: {
      Authorization: `Bearer ${await getOrFetchToken()}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(inputs),
  });
  return resp;
}

export async function archiveInboxNote(id: string): Promise<Response> {
  const inputs = { archived: true };
  const resp = await fetch(fullAuthUrl(`/inbox-notes/${id}/`), {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${await getOrFetchToken()}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(inputs),
  });
  return resp;
}

export async function snoozeInboxNote(id: string): Promise<Response> {
  const resp = await fetch(fullAuthUrl(`/inbox-notes/${id}/snooze/`), {
    method: "PUT",
    headers: {
      Authorization: `Bearer ${await getOrFetchToken()}`,
      "Content-Type": "application/json",
    },
  });
  return resp;
}

export interface InboxNotesResponse {
  results: types.InboxNote[];
  next: string | null;
  previous: string | null;
}

export async function createReference(inputs: types.ReferenceCardInputs): Promise<Response> {
  const resp = await fetch(fullAuthUrl("/references/"), {
    method: "POST",
    headers: {
      Authorization: `Bearer ${await getOrFetchToken()}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(inputs),
  });
  return resp;
}

export async function createReferenceNote(
  referenceID: string,
  inputs: types.ReferenceNoteCardInputs
): Promise<Response> {
  const resp = await fetch(fullAuthUrl(`/references/${referenceID}/notes/`), {
    method: "POST",
    headers: {
      Authorization: `Bearer ${await getOrFetchToken()}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(inputs),
  });
  return resp;
}

export async function retrieveEvergreen(id: string): Promise<types.EvergreenCardWithLinks> {
  const resp = await fetch(fullAuthUrl(`/evergreens/${id}/`), {
    method: "GET",
    headers: {
      Authorization: `Bearer ${await getOrFetchToken()}`,
    },
  });
  const card = await resp.json();
  return card;
}

export async function addLinkToEvergreen(
  fromId: string,
  toCardType: types.CardTypes,
  toCardId: string
): Promise<Response> {
  const endpoint = `/evergreens/${fromId}/links/`;
  const inputs = { card_type: toCardType, card: toCardId };
  return await fetch(fullAuthUrl(endpoint), {
    method: "PUT",
    headers: {
      Authorization: `Bearer ${await getOrFetchToken()}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(inputs),
  });
}

export async function addLinkToReference(
  fromId: string,
  toCardType: types.CardTypes,
  toCardId: string
): Promise<Response> {
  const endpoint = `/references/${fromId}/links/`;
  const inputs = { card_type: toCardType, card: toCardId };
  return await fetch(fullAuthUrl(endpoint), {
    method: "PUT",
    headers: {
      Authorization: `Bearer ${await getOrFetchToken()}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(inputs),
  });
}

export async function retrieveReference(id: string): Promise<types.ReferenceCardWithLinks> {
  const resp = await fetch(fullAuthUrl(`/references/${id}/`), {
    method: "GET",
    headers: {
      Authorization: `Bearer ${await getOrFetchToken()}`,
    },
  });
  const card = await resp.json();
  return card;
}

export async function updateReference(
  id: string,
  values: Partial<types.ReferenceCardEditable>
): Promise<types.ReferenceCardWithLinks> {
  const resp = await fetch(fullAuthUrl(`/references/${id}/`), {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${await getOrFetchToken()}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(values),
  });
  const card = await resp.json();
  return card;
}

export async function searchBooks(query: string): Promise<Response> {
  const baseUrl = fullAuthUrl("/books-search/");
  const queryParams = new URLSearchParams();
  queryParams.append("q", query);
  const url = baseUrl + "?" + queryParams.toString();
  return await fetch(url, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${await getOrFetchToken()}`,
    },
  });
}

export async function search(query: string): Promise<Response> {
  const queryParams = new URLSearchParams();
  queryParams.append("q", query);
  const url = fullAuthUrl("/search/") + `?${queryParams.toString()}`;
  return await fetch(url, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${await getOrFetchToken()}`,
    },
  });
}

export async function instapaperAccountDetails(): Promise<Response> {
  return await fetch(fullAuthUrl("/integrations/instapaper/"), {
    method: "GET",
    headers: {
      Authorization: `Bearer ${await getOrFetchToken()}`,
    },
  });
}

export async function instapaperAccountConnect(
  username: string,
  password: string
): Promise<Response> {
  return await fetch(fullAuthUrl("/integrations/instapaper/connect/"), {
    method: "POST",
    headers: {
      Authorization: `Bearer ${await getOrFetchToken()}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ username, password }),
  });
}

export function request<T>(endpoint: string): Promise<T> {
  return getOrFetchToken()
    .then(token => {
      const url = fullAuthUrl(endpoint);
      const options = {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      };
      return fetch(url, options);
    })
    .then(resp => {
      return resp.json() as Promise<T>;
    });
}
